import { formatMakeDisplay, formatModelDisplay } from '@/utils/makeDisplay';
import { CheckCircle, Star, Shield, TrendUp as TrendingUp } from '@phosphor-icons/react';
import { useParams, Link, useNavigate } from "@/lib/router-compat";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

import { SEOHreflang } from "@/components/seo/SEOHreflang";
import { ModernListingCard } from "@/components/listings/card/ModernListingCard";
import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getBrandContent } from '@/data/brandSEOContent';
import { SEOFooterContent } from "@/components/seo/SEOFooterContent";
import { getRouteSeo } from "@/lib/seo/route-head";
import { isEligibleCityListing } from "@/utils/cityArea";
import { detectCity, filterMakeModel, fuelPageCanonicalPath } from "@/utils/makeModelFilter";
import { ModelStatsContent } from "@/components/seo/ModelStatsContent";
import { ModelEditorialContent } from "@/components/seo/ModelEditorialContent";
import { getModelPage, matchesModelPage, modelPagesForMake, modelHeading, displayModelName } from "@/data/modelPages";
import { useCountry } from "@/hooks/useCountry";
import { useTranslation } from "@/hooks/useTranslation";
import { useListingsV2, useMakeModelDict } from "@/hooks/useListingsV2";
import { useScrollToResults } from "@/hooks/useScrollToResults";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

type Car = {
  id: string;
  slug: string;
  title: string;
  price: number;
  currency?: string;
  year: number;
  mileageKm: number;
  location: string;
  photo?: string;
  fuel?: string;
  /** Main listings_v2.fuel_type, used by the /cars fuel filter. */
  fuelCode?: string | null;
  transmission?: string;
  make?: string;
  model?: string;
  status?: string;
  condition?: string;
  user_id?: string;
};

// Known car makes to distinguish from cities
const KNOWN_MAKES = ['dacia', 'bmw', 'mercedes', 'mercedes-benz', 'audi', 'toyota', 'volkswagen', 'ford', 'opel', 'renault', 'skoda', 'hyundai', 'kia', 'nissan', 'mazda', 'honda', 'peugeot', 'citroen', 'fiat', 'volvo', 'seat'];

const MakeModelPage = () => {
  const { make, model, fuel } = useParams();
  const navigate = useNavigate();
  const { country, config } = useCountry();
  const { t, language } = useTranslation();
  const pathPrefix = config.path === '/' ? '' : config.path;

  // Use Listings V2 (correlated with DB) and filter by make + country
  const normalizedMakeForRpc = make ? make.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : undefined;
  const { data: v2Data = [], isLoading } = useListingsV2({
    country_code: country,
    make: normalizedMakeForRpc,
    limit: 200,
  });
  const { data: mmDict = [] } = useMakeModelDict();

  // Redirect to correct category based on make (DB via mm_dict, with smart fallback)
  useEffect(() => {
    if (!make) return;
    const normalize = (s: string) => s.toLowerCase().replace(/[-\s]/g, '');
    
    // Find all matching entries for this make (could be multiple categories)
    const matchingEntries = (mmDict as any[]).filter((d) => normalize(d.make || '') === normalize(make));
    
    // Prioritize 'cars' category if multiple matches exist
    let entry = matchingEntries.find((d) => d.categories?.code === 'cars');
    if (!entry && matchingEntries.length > 0) {
      entry = matchingEntries[0]; // fallback to first match
    }
    
    let code = entry?.categories?.code as string | undefined;

    // Fallback map for well-known non-car brands, used if mm_dict lacks the make
    if (!code || code === 'car') {
      const fallback: Record<string, 'motorcycle' | 'boat' | 'truck' | 'agricultural' | 'construction'> = {
        yamaha: 'motorcycle',
        suzuki: 'motorcycle',
        kawasaki: 'motorcycle',
        ducati: 'motorcycle',
        ktm: 'motorcycle',
        harley: 'motorcycle',
        'harley-davidson': 'motorcycle',
        bayliner: 'boat',
        quicksilver: 'boat',
        jeanneau: 'boat',
        beneteau: 'boat',
      };
      const fb = fallback[normalize(make)];
      if (fb) code = fb;
    }

    if (!code || code === 'car') return;

    const basePath = config.path === '/' ? '' : config.path;
    let route = '/cars';
    if (code === 'motorcycle') route = '/motociclete';
    else if (code === 'boat') route = '/ambarcatiuni';
    else if (code === 'truck') route = '/camioane';
    else if (code === 'agricultural' || code === 'construction') route = '/utilaje';
    else return;

    const params = new URLSearchParams();
    params.set('make', entry?.make || make);
    if (model) params.set('model', model);

    navigate(`${basePath}${route}?${params.toString()}`, { replace: true });
  }, [make, model, mmDict, navigate, config.path]);
  // Map Listings V2 to the UI Card shape
  const cars: Car[] = useMemo(() => {
    // Same rule as the brand directory and city pages: active passenger cars only.
    return (v2Data || []).filter((d: any) => isEligibleCityListing(d)).map((d: any) => {
      const carMake = d.make || '';
      const carModel = d.model || '';
      // Display-only name (e.g. Yaris Cross imported as "Yaris"); slug/filters keep the stored model.
      const shownModel = displayModelName(d);
      const year = d.year || 0;
      const slug = `${carMake}-${carModel}-${year}-${d.id}`
        .toLowerCase()
        .replace(/\s+/g, '-');
      const location = [d.location_city, d.location_region, d.location_country]
        .filter(Boolean)
        .join(', ');

      return {
        id: d.id,
        slug,
        title: d.title || `${carMake} ${shownModel}`.trim(),
        price: Number(d.price) || 0,
        currency: d.currency,
        year: year,
        mileageKm: d.mileage_value ?? 0,
        location: location || '',
        photo: Array.isArray(d.photos) && d.photos.length > 0 ? d.photos[0] : undefined,
        fuel: (d.attributes && d.attributes.fuel_type) || undefined,
        fuelCode: d.fuel_type ?? null,
        transmission: (d.attributes && d.attributes.transmission) || undefined,
        make: carMake || undefined,
        model: carModel || undefined,
        status: d.status || 'active',
        condition: d.condition,
        user_id: d.user_id,
      };
    });
  }, [v2Data]);

  // Verified-company badge only for dealers that passed the check (never for every card).
  const verifiedIds = useMemo(
    () => new Set((v2Data || []).filter((d: any) => d.dealer_verified === true).map((d: any) => d.id)),
    [v2Data],
  );

  // Curated model page (e.g. Toyota Yaris Cross): strict model match, never a sibling model.
  const modelPage = country === 'RO' && !fuel ? getModelPage(make, model) : null;
  const modelPageIds = useMemo(
    () =>
      modelPage
        ? new Set((v2Data || []).filter((d: any) => matchesModelPage(modelPage, d)).map((d: any) => d.id))
        : null,
    [v2Data, modelPage],
  );

  // Detect if model param is actually a city
  const detectedCity = useMemo(() => detectCity(model), [model]);

  // Filter cars based on URL parameters
  const filteredCars = useMemo(() => {
    if (modelPageIds) return cars.filter((c) => modelPageIds.has(c.id));
    return filterMakeModel(cars, { make, model, fuel });
  }, [cars, make, model, fuel, modelPageIds]);

  // If exactly one result for a specific make+model (or make+model+fuel), go to detail page.
  // Curated model pages stay on the page so the guide and links remain reachable.
  useEffect(() => {
    if (!modelPage && !isLoading && filteredCars.length === 1 && make && model) {
      const only = filteredCars[0];
      navigate(`/cars/details/${only.id}`);
    }
  }, [isLoading, filteredCars, make, model, navigate]);


  const makeLower = (make || "").toLowerCase();
  // Same rule as the SEO title in the route head (src/data/modelPages.ts).
  const modelHead = modelPage ? modelHeading(modelPage, filteredCars.map((c) => c.condition ?? null)) : null;
  const modelH1 = modelHead?.h1 ?? '';
  // Get brand content in the current language
  const localizedBrandContent = getBrandContent(language);
  const currentBrand = localizedBrandContent[makeLower];
  
  // Create dynamic title based on parameters
  const roBrandSeo = country === 'RO' && make && !model && !fuel ? getRouteSeo('/cars/$make', { make }) : null;
  const getDynamicTitle = () => {
    if (roBrandSeo) return `${roBrandSeo.title} | RO Auto Market`;
    if (modelPage) return `${modelH1} | RO Auto Market`;
    const makeCapitalized = formatMakeDisplay(make);
    const locationName = config.name;
    
    // If model is a city, show "Brand CITY" format
    if (detectedCity) {
      return `${makeCapitalized} ${detectedCity.toUpperCase()} ${t('makemodel_second_hand')}`;
    }
    
    if (model && fuel) {
      return `${makeCapitalized} ${model} ${fuel} ${t('makemodel_second_hand')} ${locationName}`;
    } else if (model) {
      // Check if model is actually a fuel type
      const fuelTypes = ['hibrid', 'electric', 'diesel', 'benzina'];
      const isModelFuelType = fuelTypes.includes(model.toLowerCase());
      if (isModelFuelType) {
        return `${makeCapitalized} ${formatModelDisplay(model)} ${t('makemodel_second_hand')} ${locationName}`;
      }
      return `${makeCapitalized} ${model} ${t('makemodel_second_hand')} ${locationName}`;
    }
    return currentBrand?.title || `${makeCapitalized} ${t('makemodel_second_hand')} ${locationName}`;
  };
  
  const getDynamicDescription = () => {
    if (roBrandSeo) return roBrandSeo.description;
    if (modelPage) return modelPage.description;
    const makeCapitalized = formatMakeDisplay(make);
    const locationName = detectedCity || config.name;
    if (filteredCars.length === 0) {
      return `${makeCapitalized} ${detectedCity || model || ''} ${t('makemodel_second_hand')} ${locationName}. ${t('makemodel_filter_and_choose')}`;
    }
    const count = filteredCars.length > 99 ? '99+' : filteredCars.length;
    return `${count} ${makeCapitalized} ${detectedCity ? `în ${detectedCity}` : model || ''}. ${t('makemodel_filter_and_choose')}`;
  };

  // Fuel pages: same canonical as the server head (own path, or the parent model page when identical).
  const canonicalUrl = make && model && fuel
    ? `https://roautomarket.ro${pathPrefix}${fuelPageCanonicalPath(v2Data as never[], { make, model, fuel })}`
    : `https://roautomarket.ro${pathPrefix}/cars/${make}${model ? `/${model}` : ''}`;
  
  // Schema.org structured data
  // Schema.org structured data - valid CollectionPage with ItemList
  const structuredData = currentBrand ? {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": `${canonicalUrl}#webpage`,
    "name": (roBrandSeo ? getDynamicTitle() : currentBrand?.title || getDynamicTitle()),
    "description": (roBrandSeo ? getDynamicDescription() : currentBrand?.description || getDynamicDescription()),
    "url": canonicalUrl,
    "isPartOf": {
      "@type": "WebSite",
      "@id": "https://roautomarket.ro/#website",
      "name": "RO Auto Market",
      "url": "https://roautomarket.ro"
    },
    "mainEntity": {
      "@type": "ItemList",
      "name": `${t('makemodel_cars')} ${formatMakeDisplay(make)} ${t('makemodel_second_hand')}`,
      "numberOfItems": currentBrand.models?.length || 0,
      "itemListElement": currentBrand.models?.map((modelData, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "Product",
          "name": modelData.name,
          "description": modelData.description,
          "category": t('vehicle_types.cars'),
          "brand": {
            "@type": "Brand",
            "name": formatMakeDisplay(make)
          }
        }
      })) || []
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": t('makemodel_home') || 'Acasă',
          "item": `https://roautomarket.ro${pathPrefix || '/'}`
        },
        {
          "@type": "ListItem", 
          "position": 2,
          "name": t('makemodel_cars') || 'Mașini',
          "item": `https://roautomarket.ro${pathPrefix}/cars`
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": formatMakeDisplay(make),
          "item": canonicalUrl
        }
      ]
    }
  } : null;

  // When the visitor lands here from a search (query params or #results),
  // jump straight to the vehicles instead of the SEO intro.
  useScrollToResults({
    key: typeof window !== 'undefined' ? window.location.pathname + window.location.search : '',
    active:
      typeof window !== 'undefined' &&
      (window.location.search.length > 1 || window.location.hash === '#results'),
    ready: !isLoading,
  });

  return (

    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{((roBrandSeo ? getDynamicTitle() : currentBrand?.title || getDynamicTitle())).slice(0, 60)}</title>
        <meta name="description" content={((roBrandSeo ? getDynamicDescription() : currentBrand?.description || getDynamicDescription())).slice(0, 155)} />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Open Graph */}
        <meta property="og:title" content={(roBrandSeo ? getDynamicTitle() : currentBrand?.title || getDynamicTitle())} />
        <meta property="og:description" content={(roBrandSeo ? getDynamicDescription() : currentBrand?.description || getDynamicDescription())} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://roautomarket.ro/og-roautomarket.png" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@roautomarket" />
        <meta name="twitter:title" content={(roBrandSeo ? getDynamicTitle() : currentBrand?.title || getDynamicTitle())} />
        <meta name="twitter:description" content={(roBrandSeo ? getDynamicDescription() : currentBrand?.description || getDynamicDescription())} />
        <meta name="twitter:image" content="https://roautomarket.ro/og-roautomarket.png" />
        
        {/* Structured Data */}
        {structuredData && (
          <script type="application/ld+json">
            {JSON.stringify(structuredData)}
          </script>
        )}
      </Helmet>
      <SEOHreflang canonicalUrl={canonicalUrl} />

      <main className="container mx-auto px-4 py-8">
        <div className="surface-dark band-bleed -mt-8 pt-3 pb-5 md:pb-8 mb-8">
        {/* Breadcrumbs */}
        <Breadcrumb className="mb-3 md:mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href={pathPrefix || '/'}>{t('makemodel_home')}</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              {country === 'RO' && (!model || modelPage) ? (
                <BreadcrumbLink href="/masini-second-hand">Mașini pe mărci și orașe</BreadcrumbLink>
              ) : (
                <BreadcrumbLink href={`${pathPrefix}/cars`}>{t('makemodel_cars')}</BreadcrumbLink>
              )}
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            {modelPage && (
              <>
                <BreadcrumbItem>
                  <BreadcrumbLink href={`/cars/${modelPage.makeSlug}`}>{formatMakeDisplay(make)}</BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
              </>
            )}
            <BreadcrumbItem>
              <BreadcrumbPage>{modelPage ? modelPage.display : model ? `${formatMakeDisplay(make)} ${model}` : formatMakeDisplay(make)}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Hero Section */}
        {currentBrand ? (
          <div className="text-left md:text-center">
            <Badge variant="secondary" className="hidden md:inline-flex mb-4">
              {currentBrand.badge}
            </Badge>
            
            <h1 className="text-2xl md:text-5xl font-bold text-foreground mb-2 md:mb-4">
              {currentBrand.h1}
            </h1>
            
            <p className="text-sm md:text-xl text-muted-foreground max-w-3xl mx-auto mb-3 md:mb-8">
              {currentBrand.intro}
            </p>

            <div className="flex md:justify-center">
              <Button size="lg" asChild>
                <Link to={`/cars?make=${makeLower}`}>{t('makemodel_view_all_make')} {formatMakeDisplay(make)}</Link>
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-left md:text-center">
            <h1 className="text-2xl md:text-4xl font-bold mb-2 md:mb-4">
              {modelPage
                ? modelH1
                : country === 'RO' && !model
                ? `${formatMakeDisplay(make)} de vânzare`
                : <>{t('makemodel_cars')} {formatMakeDisplay(make)} {model ? model.toUpperCase() : ''} {t('makemodel_second_hand')}</>}
            </h1>
            {modelPage ? (
              <p className="text-sm md:text-base text-muted-foreground max-w-2xl mx-auto mb-3 md:mb-6">
                {modelHead?.note} Doar anunțuri {modelPage.display}, fără alte modele {formatMakeDisplay(make)}.
              </p>
            ) : (
            <p className="text-sm md:text-base text-muted-foreground max-w-2xl mx-auto mb-3 md:mb-6">
              {t('makemodel_results_for')} {formatMakeDisplay(make)}{model ? ` ${model}` : ''}. {t('makemodel_filter_and_choose')}
            </p>
            )}
            {country === 'RO' && !model && modelPagesForMake(make).length > 0 && (
              <p className="text-sm text-muted-foreground mb-3 md:mb-6">
                Modele:{' '}
                {modelPagesForMake(make).map((p) => (
                  <Link key={p.modelSlug} to={`/cars/${p.makeSlug}/${p.modelSlug}`} className="text-primary underline">{p.display}</Link>
                ))}
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-2 md:gap-4 md:justify-center">
              <Button size="lg" asChild>
                <Link to={`${pathPrefix}/cars?make=${makeLower}`}>{t('makemodel_view_all_make')} {formatMakeDisplay(make)}</Link>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link to={`${pathPrefix}/cars`}>{t('makemodel_view_all_cars')}</Link>
              </Button>
            </div>
          </div>
        )}
        </div>
          {!currentBrand && (
            <div id="results" data-results className="mb-8 scroll-mt-24">
              <h2 className="text-2xl font-bold mb-4">
                {isLoading ? t('makemodel_loading') : 
                 filteredCars.length > 0 ? `${filteredCars.length} ${t('makemodel_cars_found')}` : 
                 t('makemodel_no_cars_found')}
              </h2>
              
              {isLoading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="animate-pulse">
                      <div className="bg-gray-300 h-48 rounded-lg mb-4"></div>
                      <div className="bg-gray-300 h-4 rounded-sm mb-2"></div>
                      <div className="bg-gray-300 h-4 rounded-sm mb-2 w-2/3"></div>
                    </div>
                  ))}
                </div>
              ) : filteredCars.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                  {filteredCars.map((car) => (
                    <ModernListingCard
                      key={car.id} 
                      id={car.id}
                      title={car.title}
                      price={car.price}
                      currency={car.currency}
                      year={car.year}
                      mileageKm={car.mileageKm}
                      location={car.location}
                      photo={car.photo}
                      slug={car.slug}
                      make={car.make}
                      model={car.model}
                      verified_dealer={verifiedIds.has(car.id)}
                      vin_available={false}
                      certified_vehicle={false}
                      status={car.status as any}
                      user_id={car.user_id}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-lg text-muted-foreground mb-4">
                    {modelPage
                      ? `Momentan nu există anunțuri active ${modelPage.display}.`
                      : <>{t('makemodel_currently_no_cars')} {formatMakeDisplay(make)} {model || ''} {t('makemodel_available')}</>}
                  </p>
                  <div className="flex gap-4 justify-center">
                    <Button asChild>
                      <Link to={`${pathPrefix}/cars?make=${makeLower}`}>{t('makemodel_view_all_make')} {formatMakeDisplay(make)}</Link>
                    </Button>
                    <Button variant="outline" asChild>
                      <Link to={`${pathPrefix}/cars`}>{t('makemodel_view_all_cars')}</Link>
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Show brand content only when we have brand content available */}
          {currentBrand && (
            <>
              {/* Display any found cars first if available */}
              {filteredCars.length > 0 && (
                <div id="results" data-results className="mb-12 scroll-mt-24">

                  <h2 className="text-2xl font-bold mb-6">{filteredCars.length} {t('makemodel_cars_available')} {formatMakeDisplay(make)} {t('makemodel_available')}</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                    {filteredCars.map((car) => (
                    <ModernListingCard
                      key={car.id} 
                      id={car.id}
                      title={car.title}
                      price={car.price}
                      currency={car.currency}
                      year={car.year}
                      mileageKm={car.mileageKm}
                      location={car.location}
                      photo={car.photo}
                      slug={car.slug}
                      make={car.make}
                      model={car.model}
                      verified_dealer={verifiedIds.has(car.id)}
                      vin_available={false}
                      certified_vehicle={false}
                      status={car.status as any}
                      user_id={car.user_id}
                    />
                  ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Stats - only show for brand pages with brand content */}
          {currentBrand && currentBrand.stats.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
              {currentBrand.stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-2xl font-bold text-primary">{stat.value}</div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </div>
              ))}
            </div>
          )}

        {/* Popular Models */}
        {currentBrand && (
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-center mb-8">{t('makemodel_popular_models')} {formatMakeDisplay(make)} {t('makemodel_popular')}</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {currentBrand.models.map((modelData, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Star className="text-yellow-500" size={20} />
                      {modelData.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">{modelData.description}</p>
                    <ul className="space-y-2">
                      {modelData.features?.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm">
                          <CheckCircle className="text-green-600" size={16} />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Button variant="outline" className="w-full mt-4" asChild>
                      <Link to={`/cars?make=${makeLower}&model=${modelData.name.split(' ')[1]?.toLowerCase()}`}>
                        {t('makemodel_view_model')} {modelData.name}
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}

        {/* Why Choose Section */}
        {currentBrand && (
          <section className="mb-12">
            <div className="grid md:grid-cols-2 gap-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="text-green-600" />
                    {t('makemodel_why_choose')} {formatMakeDisplay(make)} {t('makemodel_second_hand')}?
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {currentBrand.whyChoose?.map((reason, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <CheckCircle className="text-green-600 mt-0.5" size={16} />
                        <span className="text-sm">{reason}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="text-blue-600" />
                    {t('makemodel_buying_guide')} {formatMakeDisplay(make)}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {currentBrand.tips?.map((tip, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <CheckCircle className="text-blue-600 mt-0.5" size={16} />
                        <span className="text-sm">{tip}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </section>
        )}

        {/* FAQ Section */}
        {currentBrand && (
          <section className="mb-12">
            <Card>
              <CardHeader>
                <CardTitle className="text-center">{t('makemodel_faq_title')} {formatMakeDisplay(make)} {t('makemodel_faq_second_hand')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {currentBrand.faqs?.map((faq, index) => (
                  <div key={index}>
                    <h3 className="font-semibold mb-2">{faq.question}</h3>
                    <p className="text-muted-foreground text-sm">{faq.answer}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </section>
        )}

        {/* Curated model guide (below the listings) */}
        {modelPage && (
          <section className="mb-12" aria-labelledby="model-guide">
            <Card>
              <CardContent className="p-5 md:p-8">
                <h2 id="model-guide" className="text-xl md:text-2xl font-semibold mb-4">{modelPage.guideTitle}</h2>
                {modelPage.guide.map((s) => (
                  <div key={s.h} className="mt-5">
                    <h3 className="sans-heading text-base md:text-lg font-semibold mb-1">{s.h}</h3>
                    <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                      {s.p}
                      {s.sources?.map((n) => (
                        <sup key={n} className="ml-0.5"><a href={`#sursa-${n}`} className="text-primary">[{n}]</a></sup>
                      ))}
                    </p>
                  </div>
                ))}
                <p className="mt-6 text-sm">
                  <Link to={`/cars/${modelPage.makeSlug}`} className="text-primary underline">Toate anunțurile {formatMakeDisplay(make)}</Link>
                  {' · '}
                  <Link to="/masini-second-hand" className="text-primary underline">Mașini pe mărci și orașe</Link>
                </p>
                <h3 className="sans-heading text-sm font-semibold mt-6 mb-2">Surse</h3>
                <ol className="list-decimal pl-5 space-y-1 text-xs md:text-sm text-muted-foreground">
                  {modelPage.sources.map((src, i) => (
                    <li key={src.url} id={`sursa-${i + 1}`}>
                      <a href={src.url} target="_blank" rel="noopener noreferrer" className="underline break-words">{src.label}</a>
                    </li>
                  ))}
                </ol>
              </CardContent>
            </Card>
          </section>
        )}

        {/* CTA Section */}
        <div className="text-center bg-gradient-to-r from-primary/10 to-primary/5 rounded-lg p-8 border">
          <h2 className="text-2xl font-semibold mb-4">{t('makemodel_ready_to_find')} {formatMakeDisplay(make)} {t('makemodel_perfect')}</h2>
          <p className="text-muted-foreground mb-6">
            {t('makemodel_explore_cars')} {formatMakeDisplay(make)} {t('makemodel_from_verified_dealers')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild>
              <Link to={`/cars?make=${makeLower}`}>🚗 {t('makemodel_view_all_available')} {formatMakeDisplay(make)} {t('makemodel_disponibile')}</Link>
            </Button>
            <Button variant="outline" size="lg" asChild>
              <Link to="/dealeri-verificati">🤝 {t('makemodel_contact_dealers')}</Link>
            </Button>
          </div>
         </div>

         {/* Stock statistics block ("prețuri de la", average price) hidden until price,
             currency and calculation rules are validated. */}

         {/* Model-level stats + FAQ (auto-generated from live stock, works for any make+model).
             Curated model pages skip it: it shows price ranges/medians not yet validated. */}
         {country === 'RO' && model && !detectedCity && !fuel && !modelPage && filteredCars.length >= 1 && (
           <ModelStatsContent
             cars={filteredCars}
             make={make || ''}
             model={model}
           />
         )}

         {/* Editorial long-form content for high-priority models (Golf 6 etc.) */}
         {country === 'RO' && model && !detectedCity && !fuel && (
           <ModelEditorialContent make={make || ''} model={model} />
         )}

         {/* SEO Footer Content */}
         <SEOFooterContent brandSlug={!model ? makeLower : undefined} />
       </main>
     </div>
   );
 };

 export default MakeModelPage;